import { ChangeDetectionStrategy, Component } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { combineLatest, pipe } from "rxjs";
import { map, switchMap, take, tap } from "rxjs/operators";

import { ResetPasswordRequestParams } from "@app/auth/model/auth-user";
import { AuthUserService } from "@app/auth/services/auth-user.service";
import { AuthErrorCode } from "@app/auth/store/effects";
import { messages } from "@app/shared/resources/messages";
import { passwordValid } from "@app/shared/validators";
import { validateMatchingPwd } from "@app/shared/validators/password-mismatch";

@Component({
    selector: "app-reset-password",
    templateUrl: "./reset-password.component.html",
    styleUrls: ["./reset-password.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordComponent {
    public newPwdRequest: ResetPasswordRequestParams = null;
    public pwdForm: FormGroup = this.fb.group(
        {
            pwd: ["", [passwordValid]],
            confirmPwd: [""],
        },
        {
            validators: validateMatchingPwd,
        }
    );

    private readonly errorCode$ = this.authUserService.selectSignInErrorCode$();
    private readonly isPending$ = this.authUserService.selectPendingSignInState$();

    public errorMessage$ = this.authUserService.selectSignInError$();
    public mismatchErr$ = this.pwdForm.valueChanges.pipe(map(_ => this.getMismatchError(this.pwdForm.errors?.mismatch)));
    public isPendingValidation$ = this.authUserService.selectCurrentUrlQueryParams$().pipe(
        // Taking more than one will cause an error after successful reset and the page begins to redirect
        take(1),
        this.verifyCodeAndEnableFormPipe
    );

    constructor(private readonly fb: FormBuilder, private readonly authUserService: AuthUserService) {}

    get disableSubmit() {
        return !this.pwdForm.enabled;
    }

    get fields() {
        return this.pwdForm.controls;
    }

    private get verifyCodeAndEnableFormPipe() {
        return pipe(
            tap(({ mode, oobCode, lang, continueUrl }) => {
                this.newPwdRequest = { oobCode, lang, mode, continueUrl };
                this.verifyOobCode(this.newPwdRequest);
            }),
            switchMap(_ => combineLatest([this.errorCode$, this.isPending$])),
            map(([errorCode, isPending]) => {
                if (errorCode === AuthErrorCode.tokenNotValid) {
                    this.pwdForm.disable();
                } else {
                    this.pwdForm.enable();
                }

                return isPending;
            })
        );
    }

    private verifyOobCode(requestData: ResetPasswordRequestParams) {
        this.authUserService.dispatchVerifyPassword(requestData);
    }

    changePwd(form: FormGroup, requestData: ResetPasswordRequestParams) {
        Object.values(this.pwdForm.controls).forEach(control => {
            control.markAsTouched();
            control.markAsDirty();
        });

        if (form.valid) {
            this.pwdForm.disable();
            requestData.newPassword = form.value.pwd;
            this.authUserService.dispatchChangePassword(requestData);
            return;
        }

        this.authUserService.dispatchError("Invalid submission. Please try again.");
    }

    /**
     *
     */
    gotoResetPasswordPage() {
        this.authUserService.clearLoginErrorState();
    }

    /**
     * Push the password mismatch error on the form to the password confirm field.
     * Allows the error to be displayed using the app-valudation-message component.
     *
     * @param mismatchError
     */
    getMismatchError(mismatchError: boolean) {
        const error = mismatchError ? { passwordsNotEqual: messages.validation.passwordsNotEqual } : null;
        this.pwdForm.controls.confirmPwd.setErrors(error);
        return this.pwdForm.controls.confirmPwd.errors;
    }
}
